import { relative } from "node:path";
import { Component, Project, YamlFile } from "projen";
import { ValueOf } from "type-fest";

/**
 * Predefined minimum release age values in minutes.
 */
export const MINIMUM_RELEASE_AGE = {
  ZERO_DAYS: 0,
  ONE_HOUR: 60,
  SIX_HOURS: 360,
  TWELVE_HOURS: 720,
  ONE_DAY: 1440,
  TWO_DAYS: 2880,
  THREE_DAYS: 4320,
  FOUR_DAYS: 5760,
  FIVE_DAYS: 7200,
  SIX_DAYS: 8640,
  ONE_WEEK: 10080,
};

/**
 * Platform selectors used by `supportedArchitectures` to widen the set of
 * optional dependencies pnpm downloads beyond the current host.
 *
 * See: https://pnpm.io/settings#supportedarchitectures
 */
export interface PnpmSupportedArchitectures {
  /**
   * Operating systems to fetch optional dependencies for, e.g.
   * `["current", "win32", "linux"]`.
   */
  readonly os?: Array<string>;

  /**
   * CPU architectures to fetch optional dependencies for, e.g.
   * `["current", "x64", "arm64"]`.
   */
  readonly cpu?: Array<string>;

  /**
   * libc implementations to fetch optional dependencies for, e.g.
   * `["current", "glibc", "musl"]`.
   */
  readonly libc?: Array<string>;
}

/**
 * Rules that relax pnpm's peer-dependency diagnostics.
 *
 * See: https://pnpm.io/settings#peerdependencyrules
 */
export interface PnpmPeerDependencyRules {
  /**
   * Peer dependency names (glob patterns allowed) whose "missing peer"
   * warnings are suppressed.
   */
  readonly ignoreMissing?: Array<string>;

  /**
   * Peer dependency names (glob patterns allowed) that may resolve to any
   * version without a version-mismatch warning.
   */
  readonly allowAny?: Array<string>;

  /**
   * Additional version ranges accepted for a peer dependency, keyed by
   * package name or by `parent>child` selector.
   */
  readonly allowedVersions?: { [selector: string]: string };
}

/**
 * pnpm 11's `update` block, controlling `pnpm update` behavior.
 *
 * Supersedes the deprecated `updateConfig` block. When both are set, pnpm
 * ignores `updateConfig` in favor of `update` and emits a warning — so
 * configulator emits only the winning key.
 *
 * See: https://pnpm.io/settings#update
 */
export interface PnpmUpdateSettings {
  /**
   * Packages excluded from `pnpm update`. Replaces the deprecated
   * `updateConfig.ignoreDependencies`.
   */
  readonly ignoreDeps?: Array<string>;

  /**
   * Whether `pnpm update` writes a changeset file.
   */
  readonly changeset?: boolean;

  /**
   * Whether `pnpm update` also updates GitHub Actions workflow pins.
   */
  readonly githubActions?: boolean;

  /**
   * GitHub server used when resolving GitHub Actions updates.
   */
  readonly githubActionsServer?: string;
}

/**
 * The deprecated `updateConfig` block.
 *
 * @deprecated Superseded by `update` in pnpm 11. Retained so consumers on
 *             older pnpm majors keep a typed route; prefer `update`.
 *
 * See: https://pnpm.io/settings#updateconfig
 */
export interface PnpmUpdateConfigSettings {
  /**
   * Packages excluded from `pnpm update`.
   */
  readonly ignoreDependencies?: Array<string>;

  /**
   * Whether `pnpm update` writes a changeset file.
   */
  readonly changeset?: boolean;

  /**
   * Whether `pnpm update` also updates GitHub Actions workflow pins.
   */
  readonly githubActions?: boolean;

  /**
   * GitHub server used when resolving GitHub Actions updates.
   */
  readonly githubActionsServer?: string;
}

export interface PnpmWorkspaceOptions {
  /**
   * Filename for the pnpm workspace file. This should probably never change.
   *
   * @default "pnpm-workspace.yaml"
   */
  readonly fileName?: string;

  /**
   * To reduce the risk of installing compromised packages, you can delay the
   * installation of newly published versions. In most cases, malicious releases
   * are discovered and removed from the registry within an hour.
   *
   * minimumReleaseAge defines the minimum number of minutes that must pass
   * after a version is published before pnpm will install it. This applies to
   * all dependencies, including transitive ones.
   *
   * Note: this should match depsUpgradeOptions.cooldown in the project config.
   *
   * See: https://pnpm.io/settings#minimumreleaseage
   *
   * @default MINIMUM_RELEASE_AGE.ONE_WEEK
   */
  readonly minimumReleaseAge?: ValueOf<typeof MINIMUM_RELEASE_AGE>;

  /**
   * If you set minimumReleaseAge but need certain dependencies to always
   * install the newest version immediately, you can list them under
   * minimumReleaseAgeExclude. The exclusion works by package name and applies
   * to all versions of that package.
   *
   * @default - no exclusions (empty array)
   *
   * See: https://pnpm.io/settings#minimumreleaseageexclude
   */
  readonly minimumReleaseAgeExclude?: Array<string>;

  /**
   * A map of package names to booleans controlling whether each package may
   * execute "preinstall", "install", and/or "postinstall" scripts during
   * installation. Set a package's value to `true` to allow lifecycle scripts;
   * set it to `false` to silently block them without a warning.
   *
   * pnpm 11 replaced the legacy `onlyBuiltDependencies` (allow-list) and
   * `ignoredBuiltDependencies` (deny-list) settings with this single
   * `allowBuilds` map. Configulator translates the deprecated input fields
   * automatically: entries from `onlyBuiltDependencies` map to `true` and
   * entries from `ignoredBuiltDependencies` map to `false`. When a key
   * appears in both `allowBuilds` and one of the deprecated fields, the
   * explicit `allowBuilds` value wins.
   *
   * For cross-version compatibility, configulator emits BOTH the new
   * `allowBuilds` map AND the legacy `onlyBuiltDependencies` /
   * `ignoredBuiltDependencies` arrays in the generated YAML so consumers
   * on either pnpm 10 or pnpm 11 see correct lifecycle-script behavior
   * without changing their input.
   *
   * See: https://pnpm.io/settings#allowbuilds
   *
   * @default none (empty object)
   */
  readonly allowBuilds?: { [packageName: string]: boolean };

  /**
   * A list of package names that are allowed to execute "preinstall",
   * "install", and/or "postinstall" scripts during installation. Only the
   * packages listed in this array will be able to run those lifecycle scripts.
   * If onlyBuiltDependenciesFile and neverBuiltDependencies are omitted, this
   * configuration option will default to blocking all install scripts.
   *
   * You may restrict allowances to specific versions (and lists of versions
   * using a disjunction with ||). When versions are specified, only those
   * versions of the package may run lifecycle scripts:
   *
   * See: https://pnpm.io/settings#onlybuiltdependencies
   *
   * @deprecated Deprecated in pnpm 11; configulator emits both legacy and
   *             new keys for cross-version compatibility — pnpm 10 honors
   *             the legacy keys, pnpm 11 honors `allowBuilds`. Prefer
   *             `allowBuilds` as the input field going forward; entries
   *             supplied here translate to `allowBuilds[<package>] = true`.
   *
   * @default none (empty array)
   */
  readonly onlyBuiltDependencies?: Array<string>;

  /**
   * A list of package names that are NOT allowed to execute "preinstall",
   * "install", and/or "postinstall" scripts during installation and will not
   * warn or ask to be executed.
   *
   * This is useful when you want to hide the warning because you know the
   * lifecycle scripts are not needed.
   *
   * https://pnpm.io/settings#ignoredbuiltdependencies
   *
   * @deprecated Deprecated in pnpm 11; configulator emits both legacy and
   *             new keys for cross-version compatibility — pnpm 10 honors
   *             the legacy keys, pnpm 11 honors `allowBuilds`. Prefer
   *             `allowBuilds` as the input field going forward; entries
   *             supplied here translate to `allowBuilds[<package>] = false`.
   *
   * @default none (empty array)
   */
  readonly ignoredBuiltDependencies?: Array<string>;

  /**
   * Additional subproject paths to include in the workspace packages array.
   * These will be combined with any subprojects discovered via projen's
   * project.subprojects property. Paths should be relative to the project root.
   *
   * @default none (empty array)
   */
  readonly subprojects?: Array<string>;

  /**
   * Catalog of reusable dependency version ranges.
   *
   * The catalog allows you to define dependency versions as reusable constants
   * that can be referenced in package.json files using the catalog: protocol.
   *
   * Example:
   * ```yaml
   * catalog:
   *   react: ^18.0.0
   *   typescript: ^5.0.0
   * ```
   *
   * Then in package.json:
   * ```json
   * {
   *   "dependencies": {
   *     "react": "catalog:react"
   *   }
   * }
   * ```
   *
   * @default undefined (not included in output)
   *
   * See: https://pnpm.io/pnpm-workspace_yaml#catalog
   */
  readonly defaultCatalog?: { [key: string]: string };

  /**
   * Named catalogs of reusable dependency version ranges.
   *
   * Multiple named catalogs with arbitrarily chosen names can be configured under the `catalogs` key.
   *
   * Example:
   * ```yaml
   * catalogs:
   *   frontend:
   *     react: ^18.0.0
   *     typescript: ^5.0.0
   *   backend:
   *     express: ^4.18.0
   * ```
   *
   * Then in package.json:
   * ```json
   * {
   *   "dependencies": {
   *     "react": "catalog:frontend/react"
   *   }
   * }
   * ```
   *
   * @default undefined (not included in output)
   *
   * See: https://pnpm.io/catalogs
   */
  readonly namedCatalogs?: {
    [catalogName: string]: { [dependencyName: string]: string };
  };

  /**
   * Whether pnpm prompts before removing a sub-project's `node_modules`
   * directory during install. Set to `false` to skip the interactive prompt
   * so headless / non-TTY consumers (Claude Code agents, CI scripts,
   * scheduled tasks) don't abort with
   * `ERR_PNPM_ABORTED_REMOVE_MODULES_DIR_NO_TTY` mid-synth.
   *
   * Introduced in pnpm 11.0.
   *
   * @default undefined (key omitted; pnpm uses its built-in default)
   *
   * @see https://pnpm.io/settings#confirmmodulespurge
   */
  readonly confirmModulesPurge?: boolean;

  /**
   * Whether pnpm fails the install when a dependency's build script has not
   * been explicitly approved via `allowBuilds`. Set to `false` so unreviewed
   * build-script entries don't fail mid-synth in CI when configulator is
   * regenerating `allowBuilds` placeholders.
   *
   * Introduced in pnpm 10.3 (default `true` in pnpm 11).
   *
   * @default undefined (key omitted; pnpm uses its built-in default)
   *
   * @see https://pnpm.io/settings#strictdepbuilds
   */
  readonly strictDepBuilds?: boolean;

  /**
   * Whether `pnpm run` / `pnpm exec` verifies the lockfile matches
   * `node_modules` before executing the requested script. Set to `"install"`
   * (or `false`) so non-TTY consumers don't hang on the `"prompt"` default.
   *
   * Introduced in pnpm 10.x.
   *
   * @default undefined (key omitted; pnpm uses its built-in default of "prompt")
   *
   * @see https://pnpm.io/settings#verifydepsbeforerun
   */
  readonly verifyDepsBeforeRun?:
    "install" | "warn" | "error" | "prompt" | false;

  /**
   * How catalog entries are enforced when a sub-project's `package.json`
   * specifies a dependency that exists in a catalog. `"manual"` keeps the
   * existing opt-in `catalog:` protocol behavior, `"prefer"` substitutes
   * catalog versions automatically, and `"strict"` rejects any version
   * that diverges from the catalog.
   *
   * Introduced in pnpm 10.12.1.
   *
   * @default undefined (key omitted; pnpm uses its built-in default of "manual")
   *
   * @see https://pnpm.io/settings#catalogmode
   */
  readonly catalogMode?: "manual" | "prefer" | "strict";

  /**
   * Whether pnpm removes unused catalog entries from `pnpm-workspace.yaml`
   * during install. Helps keep the catalog honest when sub-projects stop
   * referencing a catalog entry.
   *
   * Introduced in pnpm 10.15.
   *
   * @default undefined (key omitted; pnpm uses its built-in default)
   *
   * @see https://pnpm.io/settings#cleanupunusedcatalogs
   */
  readonly cleanupUnusedCatalogs?: boolean;

  /**
   * Whether `minimumReleaseAge` resolution fails when no published version
   * satisfies the age constraint. `true` fails the install; `false` falls
   * back to an older version that does satisfy the age constraint.
   *
   * Either way the quarantine holds — a package younger than
   * `minimumReleaseAge` is never installed. This setting only chooses the
   * failure mode when no mature version is available: hard error, or
   * resolution to an older mature version.
   *
   * Introduced in pnpm 11.0.
   *
   * @default false
   *
   * @see https://pnpm.io/settings#minimumreleaseagestrict
   */
  readonly minimumReleaseAgeStrict?: boolean;

  /**
   * Whether `minimumReleaseAge` resolution skips the age check for packages
   * whose registry response lacks timestamp metadata. Default is `true`
   * (skip the check rather than fail), which mirrors pnpm's behavior.
   *
   * Introduced in pnpm 11.0.
   *
   * @default undefined (key omitted; pnpm uses its built-in default of true)
   *
   * @see https://pnpm.io/settings#minimumreleaseageignoremissingtime
   */
  readonly minimumReleaseAgeIgnoreMissingTime?: boolean;

  /*****************************************************************************
   *
   * DEPENDENCY GRAPH
   *
   ****************************************************************************/

  /**
   * Version overrides applied to any dependency in the graph, including
   * transitive ones that no direct dependency would otherwise let you pin.
   *
   * Keys accept pnpm's full selector syntax — a bare name (`"foo"`), a
   * range-scoped selector (`"@smithy/types@^4"`), or a parent-scoped
   * selector (`"parent>child"`). Rendered verbatim.
   *
   * ```ts
   * overrides: {
   *   "@smithy/types@^4": "4.17.2",
   *   "cli-table3>colors": "^1.4.0",
   * }
   * ```
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#overrides
   */
  readonly overrides?: { [selector: string]: string };

  /**
   * Extra fields merged into a third-party package's manifest at install
   * time — the supported way to repair a dependency that ships an incorrect
   * or incomplete `package.json` without patching it.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#packageextensions
   */
  readonly packageExtensions?: { [packageSelector: string]: unknown };

  /**
   * Patch files applied to dependencies, keyed by `name@version` and valued
   * with a repo-relative path to the `.patch` file.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#patcheddependencies
   */
  readonly patchedDependencies?: { [packageAndVersion: string]: string };

  /**
   * Directory that `pnpm patch-commit` writes patch files into.
   *
   * @default undefined (key omitted; pnpm defaults to "patches")
   *
   * @see https://pnpm.io/settings#patchesdir
   */
  readonly patchesDir?: string;

  /**
   * Whether an entry in `patchedDependencies` that matches no installed
   * package is tolerated instead of failing the install.
   *
   * @default undefined (key omitted; pnpm uses its built-in default)
   *
   * @see https://pnpm.io/settings#allowunusedpatches
   */
  readonly allowUnusedPatches?: boolean;

  /**
   * Deprecation warnings to silence, keyed by package name (or
   * `name@range`) and valued with the version range whose deprecation
   * message is suppressed.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#alloweddeprecatedversions
   */
  readonly allowedDeprecatedVersions?: { [packageName: string]: string };

  /**
   * Packages whose lifecycle scripts never run, regardless of the
   * `allowBuilds` map.
   *
   * Composes with the `allowBuilds` / legacy built-dependencies hybrid
   * emission: every entry is merged into the emitted `allowBuilds` map as
   * `false` (so the derived legacy allow/deny arrays stay consistent), and
   * the verbatim `neverBuiltDependencies` array is emitted alongside.
   * Precedence runs `onlyBuiltDependencies` → `ignoredBuiltDependencies` →
   * `neverBuiltDependencies` → explicit `allowBuilds`, so an explicit
   * `allowBuilds` entry still wins.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#neverbuiltdependencies
   */
  readonly neverBuiltDependencies?: Array<string>;

  /**
   * Optional dependencies that are never installed, by package name.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#ignoredoptionaldependencies
   */
  readonly ignoredOptionalDependencies?: Array<string>;

  /**
   * Platforms to fetch optional dependencies for beyond the current host —
   * the usual way to make a lockfile usable across CI runners and developer
   * machines with different OS/CPU/libc combinations.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#supportedarchitectures
   */
  readonly supportedArchitectures?: PnpmSupportedArchitectures;

  /**
   * Packages installed before anything else and allowed to contribute
   * configuration (hooks, patches, catalogs) to the workspace, keyed by
   * package name and valued with `version+integrity`.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#configdependencies
   */
  readonly configDependencies?: { [packageName: string]: string };

  /**
   * How pnpm picks a version when several satisfy a range.
   *
   * @default undefined (key omitted; pnpm defaults to "highest")
   *
   * @see https://pnpm.io/settings#resolutionmode
   */
  readonly resolutionMode?: "highest" | "time-based" | "lowest-direct";

  /**
   * Range prefix written for newly added dependencies, e.g. `"^"` or `"~"`.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#saveprefix
   */
  readonly savePrefix?: string;

  /**
   * Whether newly added dependencies are pinned to an exact version.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#saveexact
   */
  readonly saveExact?: boolean;

  /*****************************************************************************
   *
   * SUPPLY-CHAIN POSTURE
   *
   * Extends the `minimumReleaseAge` stance above.
   *
   ****************************************************************************/

  /**
   * Whether pnpm rejects a package whose publish-trust level has regressed
   * (e.g. a package previously published with provenance that no longer is).
   *
   * @default undefined (key omitted; pnpm uses its built-in default)
   *
   * @see https://pnpm.io/settings#trustpolicy
   */
  readonly trustPolicy?: "off" | "no-downgrade";

  /**
   * Packages exempt from `trustPolicy`, as `name` or `name@range` selectors.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#trustpolicyexclude
   */
  readonly trustPolicyExclude?: Array<string>;

  /**
   * Unix timestamp (seconds) after which `trustPolicy` stops applying, used
   * to grandfather in packages published before a cutoff.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#trustpolicyignoreafter
   */
  readonly trustPolicyIgnoreAfter?: number;

  /**
   * Whether transitive dependencies resolved through exotic (non-registry)
   * specifiers — git, tarball URL, local path — are rejected. Direct
   * dependencies are unaffected.
   *
   * @default undefined (key omitted; pnpm 11 defaults to false)
   *
   * @see https://pnpm.io/settings#blockexoticsubdeps
   */
  readonly blockExoticSubdeps?: boolean;

  /**
   * Whether `minimumReleaseAgeExclude` entries are pruned to those actually
   * resolved in the lockfile.
   *
   * @default undefined (key omitted; pnpm defaults to false)
   *
   * @see https://pnpm.io/settings#minimumreleaseageexcludeprune
   */
  readonly minimumReleaseAgeExcludePrune?: boolean;

  /*****************************************************************************
   *
   * PEER DEPENDENCIES
   *
   ****************************************************************************/

  /**
   * Whether missing peer dependencies are installed automatically.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#autoinstallpeers
   */
  readonly autoInstallPeers?: boolean;

  /**
   * Whether an unresolved peer dependency fails the install.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#strictpeerdependencies
   */
  readonly strictPeerDependencies?: boolean;

  /**
   * Whether dependents are deduplicated when they resolve peers identically.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#dedupepeerdependents
   */
  readonly dedupePeerDependents?: boolean;

  /**
   * Whether peer dependencies may resolve from the workspace root's
   * dependencies.
   *
   * @default undefined (key omitted; pnpm 11 defaults to true)
   *
   * @see https://pnpm.io/settings#resolvepeersfromworkspaceroot
   */
  readonly resolvePeersFromWorkspaceRoot?: boolean;

  /**
   * Rules that relax peer-dependency diagnostics for known-good mismatches.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#peerdependencyrules
   */
  readonly peerDependencyRules?: PnpmPeerDependencyRules;

  /*****************************************************************************
   *
   * WORKSPACE SEMANTICS
   *
   ****************************************************************************/

  /**
   * Whether dependencies satisfied by a workspace package link to it rather
   * than resolving from the registry. `"deep"` also links transitively.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#linkworkspacepackages
   */
  readonly linkWorkspacePackages?: boolean | "deep";

  /**
   * Whether a workspace package is preferred over a registry version even
   * when the registry has a higher matching version.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#preferworkspacepackages
   */
  readonly preferWorkspacePackages?: boolean;

  /**
   * Whether workspace dependencies are hard-linked into the dependent's
   * `node_modules` instead of symlinked.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#injectworkspacepackages
   */
  readonly injectWorkspacePackages?: boolean;

  /**
   * Whether injected workspace dependencies are deduplicated.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#dedupeinjecteddeps
   */
  readonly dedupeInjectedDeps?: boolean;

  /**
   * Script names after which injected workspace dependencies are re-synced.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#syncinjecteddepsafterscripts
   */
  readonly syncInjectedDepsAfterScripts?: Array<string>;

  /**
   * How workspace dependencies are written to `package.json`. `"rolling"`
   * writes `workspace:^`, `true` writes `workspace:<version>`.
   *
   * @default undefined (key omitted; pnpm defaults to "rolling")
   *
   * @see https://pnpm.io/settings#saveworkspaceprotocol
   */
  readonly saveWorkspaceProtocol?: boolean | "rolling";

  /**
   * Whether recursive commands include the workspace root project.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#includeworkspaceroot
   */
  readonly includeWorkspaceRoot?: boolean;

  /**
   * Whether the workspace uses a single shared lockfile at the root.
   *
   * @default undefined (key omitted; pnpm defaults to true)
   *
   * @see https://pnpm.io/settings#sharedworkspacelockfile
   */
  readonly sharedWorkspaceLockfile?: boolean;

  /**
   * Whether a cyclic dependency between workspace packages fails the install.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#disallowworkspacecycles
   */
  readonly disallowWorkspaceCycles?: boolean;

  /**
   * Whether workspace packages are hoisted to the root `node_modules`.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#hoistworkspacepackages
   */
  readonly hoistWorkspacePackages?: boolean;

  /**
   * Scripts every workspace package must define; a missing script fails the
   * install.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#requiredscripts
   */
  readonly requiredScripts?: Array<string>;

  /*****************************************************************************
   *
   * LAYOUT AND LOCKFILE
   *
   ****************************************************************************/

  /**
   * How `node_modules` is laid out — pnpm's symlinked store (`"isolated"`),
   * a flat npm-style tree (`"hoisted"`), or Plug'n'Play (`"pnp"`).
   *
   * @default undefined (key omitted; pnpm defaults to "isolated")
   *
   * @see https://pnpm.io/settings#nodelinker
   */
  readonly nodeLinker?: "isolated" | "hoisted" | "pnp";

  /**
   * Glob patterns hoisted into the hidden `node_modules/.pnpm/node_modules`
   * directory.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#hoistpattern
   */
  readonly hoistPattern?: Array<string>;

  /**
   * Glob patterns hoisted all the way to the root `node_modules`, making
   * them importable by any package.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#publichoistpattern
   */
  readonly publicHoistPattern?: Array<string>;

  /**
   * Whether every dependency is hoisted to the root `node_modules`,
   * reproducing npm's flat layout.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#shamefullyhoist
   */
  readonly shamefullyHoist?: boolean;

  /**
   * Whether an up-to-date lockfile short-circuits resolution.
   *
   * @default undefined (key omitted; pnpm defaults to true)
   *
   * @see https://pnpm.io/settings#preferfrozenlockfile
   */
  readonly preferFrozenLockfile?: boolean;

  /**
   * Maximum length of the peer-resolution suffix in virtual store directory
   * names, lowered when Windows path limits bite.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#peerssuffixmaxlength
   */
  readonly peersSuffixMaxLength?: number;

  /*****************************************************************************
   *
   * SCRIPTS, ENGINES, PUBLISH
   *
   ****************************************************************************/

  /**
   * Whether `pre` and `post` script hooks run around a named script.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#enableprepostscripts
   */
  readonly enablePrePostScripts?: boolean;

  /**
   * Value of `NODE_OPTIONS` for lifecycle scripts, e.g.
   * `"--max-old-space-size=4096"`.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#nodeoptions
   */
  readonly nodeOptions?: string;

  /**
   * Whether a package whose `engines` field excludes the running Node
   * version fails the install.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#enginestrict
   */
  readonly engineStrict?: boolean;

  /**
   * `pnpm update` behavior. Supersedes `updateConfig`.
   *
   * When both `update` and `updateConfig` are supplied, `update` wins and
   * only `update` is emitted — pnpm warns and ignores `updateConfig` when
   * it sees both keys, so emitting both would produce a warning on every
   * install.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#update
   */
  readonly update?: PnpmUpdateSettings;

  /**
   * `pnpm update` behavior, in the pre-pnpm-11 spelling.
   *
   * @deprecated Superseded by `update` in pnpm 11. Supplying both emits only
   *             `update`. Prefer `update` in new configs.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#updateconfig
   */
  readonly updateConfig?: PnpmUpdateConfigSettings;

  /**
   * Whether `pnpm publish` runs its git working-tree and branch checks.
   *
   * @default undefined (key omitted; pnpm defaults to true)
   *
   * @see https://pnpm.io/settings#gitchecks
   */
  readonly gitChecks?: boolean;

  /**
   * Branch `pnpm publish` is allowed to publish from.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#publishbranch
   */
  readonly publishBranch?: string;

  /**
   * Whether `pnpm publish` attaches npm provenance attestations.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#provenance
   */
  readonly provenance?: boolean;

  /**
   * Whether the README is embedded in the published package metadata.
   *
   * @default undefined (key omitted)
   *
   * @see https://pnpm.io/settings#embedreadme
   */
  readonly embedReadme?: boolean;

  /*****************************************************************************
   *
   * CATALOG MAINTENANCE
   *
   ****************************************************************************/

  /**
   * Whether pnpm prunes unused catalog entries from `pnpm-workspace.yaml`
   * during install. This is pnpm's current spelling for what
   * `cleanupUnusedCatalogs` configures.
   *
   * When both are supplied, `catalogPrune` wins and only `catalogPrune` is
   * emitted, matching pnpm's own `catalogPrune ??= cleanupUnusedCatalogs`
   * precedence. When only `cleanupUnusedCatalogs` is supplied, that key is
   * emitted unchanged so consumers on older pnpm majors keep working.
   *
   * @default undefined (key omitted; pnpm defaults to false)
   *
   * @see https://pnpm.io/settings#catalogprune
   */
  readonly catalogPrune?: boolean;

  /*****************************************************************************
   *
   * PASSTHROUGH
   *
   ****************************************************************************/

  /**
   * Arbitrary pnpm settings rendered verbatim as top-level keys in the
   * generated `pnpm-workspace.yaml`.
   *
   * This is the escape hatch for every setting configulator does not type —
   * per-user / per-machine / CI-environment settings that are deliberately
   * left untyped (proxies and TLS, fetch and retry tuning, store and cache
   * paths, CLI cosmetics, registry and auth settings), settings added by
   * future pnpm releases, and the handful of settings that do not exist in
   * the pnpm version this package targets.
   *
   * ```ts
   * additionalSettings: {
   *   networkConcurrency: 8,
   *   storeDir: "/mnt/pnpm-store",
   * }
   * ```
   *
   * A key that collides with a typed field or with a structural key
   * (`packages`, `catalog`, `catalogs`) throws at synth, naming the typed
   * option to use instead.
   *
   * @default undefined (no additional keys)
   *
   * @see https://pnpm.io/settings
   */
  readonly additionalSettings?: { [settingName: string]: unknown };
}

/**
 * Curated settings that render verbatim under their own name whenever the
 * consumer supplies a value.
 *
 * Declaration order here is emission order in the generated YAML, so the
 * output stays deterministic across synths. Every entry is verified to exist
 * in the pnpm version this package targets.
 *
 * Settings needing bespoke handling are deliberately absent:
 * `neverBuiltDependencies` (merges into the `allowBuilds` map),
 * `catalogPrune` / `cleanupUnusedCatalogs`, and `update` / `updateConfig`
 * (deprecated-alias precedence pairs).
 */
const CURATED_SETTING_KEYS = [
  // Dependency graph
  "overrides",
  "packageExtensions",
  "patchedDependencies",
  "patchesDir",
  "allowUnusedPatches",
  "allowedDeprecatedVersions",
  "ignoredOptionalDependencies",
  "supportedArchitectures",
  "configDependencies",
  "resolutionMode",
  "savePrefix",
  "saveExact",
  // Supply-chain posture
  "trustPolicy",
  "trustPolicyExclude",
  "trustPolicyIgnoreAfter",
  "blockExoticSubdeps",
  "minimumReleaseAgeExcludePrune",
  // Peer dependencies
  "autoInstallPeers",
  "strictPeerDependencies",
  "dedupePeerDependents",
  "resolvePeersFromWorkspaceRoot",
  "peerDependencyRules",
  // Workspace semantics
  "linkWorkspacePackages",
  "preferWorkspacePackages",
  "injectWorkspacePackages",
  "dedupeInjectedDeps",
  "syncInjectedDepsAfterScripts",
  "saveWorkspaceProtocol",
  "includeWorkspaceRoot",
  "sharedWorkspaceLockfile",
  "disallowWorkspaceCycles",
  "hoistWorkspacePackages",
  "requiredScripts",
  // Layout and lockfile
  "nodeLinker",
  "hoistPattern",
  "publicHoistPattern",
  "shamefullyHoist",
  "preferFrozenLockfile",
  "peersSuffixMaxLength",
  // Scripts, engines, publish
  "enablePrePostScripts",
  "nodeOptions",
  "engineStrict",
  "gitChecks",
  "publishBranch",
  "provenance",
  "embedReadme",
] as const satisfies ReadonlyArray<keyof PnpmWorkspaceOptions>;

/**
 * A curated setting name.
 */
export type PnpmCuratedSettingKey = (typeof CURATED_SETTING_KEYS)[number];

/**
 * YAML keys the emitter owns, mapped to the typed option that produces them.
 *
 * `additionalSettings` is validated against this map: a passthrough key that
 * appears here is rejected with a message naming the option to use instead.
 * Most entries are identity mappings; the exceptions are the structural keys
 * whose option name differs from the emitted key.
 */
const RESERVED_YAML_KEYS: { [yamlKey: string]: string } = {
  // Structural keys whose option name differs from the emitted YAML key.
  packages: "subprojects",
  catalog: "defaultCatalog",
  catalogs: "namedCatalogs",
  // Settings emitted by the pre-existing typed surface.
  minimumReleaseAge: "minimumReleaseAge",
  minimumReleaseAgeExclude: "minimumReleaseAgeExclude",
  onlyBuiltDependencies: "onlyBuiltDependencies",
  ignoredBuiltDependencies: "ignoredBuiltDependencies",
  allowBuilds: "allowBuilds",
  confirmModulesPurge: "confirmModulesPurge",
  strictDepBuilds: "strictDepBuilds",
  verifyDepsBeforeRun: "verifyDepsBeforeRun",
  catalogMode: "catalogMode",
  cleanupUnusedCatalogs: "cleanupUnusedCatalogs",
  minimumReleaseAgeStrict: "minimumReleaseAgeStrict",
  minimumReleaseAgeIgnoreMissingTime: "minimumReleaseAgeIgnoreMissingTime",
  // Settings with bespoke emission.
  neverBuiltDependencies: "neverBuiltDependencies",
  catalogPrune: "catalogPrune",
  update: "update",
  updateConfig: "updateConfig",
  // Every curated passthrough setting is an identity mapping.
  ...Object.fromEntries(CURATED_SETTING_KEYS.map((key) => [key, key])),
};

export class PnpmWorkspace extends Component {
  /**
   * Get the pnpm workspace component of a project. If it does not exist,
   * return undefined.
   *
   * @param project - The project to query.
   * @returns The PnpmWorkspace component if present, otherwise undefined.
   */
  public static of(project: Project): PnpmWorkspace | undefined {
    const isDefined = (c: Component): c is PnpmWorkspace =>
      c instanceof PnpmWorkspace;
    return project.root.components.find(isDefined);
  }

  /**
   * Filename for the pnpm workspace file.
   */
  public readonly fileName: string;

  /**
   * To reduce the risk of installing compromised packages, you can delay the
   * installation of newly published versions. In most cases, malicious releases
   * are discovered and removed from the registry within an hour.
   *
   * minimumReleaseAge defines the minimum number of minutes that must pass
   * after a version is published before pnpm will install it. This applies to
   * all dependencies, including transitive ones.
   *
   * Note: this should match depsUpgradeOptions.cooldown in the project config.
   *
   * See: https://pnpm.io/settings#minimumreleaseage
   */
  public minimumReleaseAge: ValueOf<typeof MINIMUM_RELEASE_AGE>;

  /**
   * If you set minimumReleaseAge but need certain dependencies to always
   * install the newest version immediately, you can list them under
   * minimumReleaseAgeExclude. The exclusion works by package name and applies
   * to all versions of that package.
   *
   * See: https://pnpm.io/settings#minimumreleaseageexclude
   */
  public minimumReleaseAgeExclude: Array<string>;

  /**
   * A map of package names to booleans controlling whether each package may
   * execute lifecycle scripts during install. `true` allows the scripts;
   * `false` silently blocks them.
   *
   * pnpm 11+ replaced `onlyBuiltDependencies` and `ignoredBuiltDependencies`
   * with this single map. Configulator translates the deprecated input
   * fields into `allowBuilds` automatically and emits both legacy and new
   * keys in the generated YAML for pnpm 10/11 cross-version compatibility.
   *
   * See: https://pnpm.io/settings#allowbuilds
   */
  public allowBuilds: { [packageName: string]: boolean };

  /**
   * A list of package names that are allowed to execute "preinstall",
   * "install", and/or "postinstall" scripts during installation. Only the
   * packages listed in this array will be able to run those lifecycle scripts.
   * If onlyBuiltDependenciesFile and neverBuiltDependencies are omitted, this
   * configuration option will default to blocking all install scripts.
   *
   * You may restrict allowances to specific versions (and lists of versions
   * using a disjunction with ||). When versions are specified, only those
   * versions of the package may run lifecycle scripts:
   *
   * See: https://pnpm.io/settings#onlybuiltdependencies
   *
   * @deprecated Deprecated in pnpm 11; configulator emits both legacy and
   *             new keys for cross-version compatibility — pnpm 10 honors
   *             the legacy keys, pnpm 11 honors `allowBuilds`. Prefer
   *             `allowBuilds` as the input field going forward; entries
   *             supplied here translate to `allowBuilds[<package>] = true`.
   */
  public onlyBuiltDependencies: Array<string>;

  /**
   * A list of package names that are NOT allowed to execute "preinstall",
   * "install", and/or "postinstall" scripts during installation and will not
   * warn or ask to be executed.
   *
   * This is useful when you want to hide the warning because you know the
   * lifecycle scripts are not needed.
   *
   * https://pnpm.io/settings#ignoredbuiltdependencies
   *
   * @deprecated Deprecated in pnpm 11; configulator emits both legacy and
   *             new keys for cross-version compatibility — pnpm 10 honors
   *             the legacy keys, pnpm 11 honors `allowBuilds`. Prefer
   *             `allowBuilds` as the input field going forward; entries
   *             supplied here translate to `allowBuilds[<package>] = false`.
   */
  public ignoredBuiltDependencies: Array<string>;

  /**
   * Additional subproject paths to include in the workspace packages array.
   * These will be combined with any subprojects discovered via projen's
   * project.subprojects property.
   */
  public subprojects: Array<string>;

  /**
   * Default catalog of reusable dependency version ranges.
   *
   * The default catalog is used to define dependency versions as reusable constants
   * that can be referenced in package.json files using the catalog: protocol.
   *
   * See:https://pnpm.io/catalogs
   */
  public defaultCatalog?: { [key: string]: string };

  /**
   * Named catalogs of reusable dependency version ranges.
   *
   * Multiple named catalogs with arbitrarily chosen names can be configured under the `catalogs` key.
   *
   * See: https://pnpm.io/catalogs
   */
  public namedCatalogs?: {
    [catalogName: string]: { [dependencyName: string]: string };
  };

  /**
   * Skip the interactive `node_modules` purge prompt so headless consumers
   * don't abort mid-synth. Introduced in pnpm 11.0.
   *
   * @see https://pnpm.io/settings#confirmmodulespurge
   */
  public confirmModulesPurge?: boolean;

  /**
   * Fail (`true`) or warn (`false`) when a dependency's build script has
   * not been explicitly approved via `allowBuilds`. Introduced in pnpm 10.3.
   *
   * @see https://pnpm.io/settings#strictdepbuilds
   */
  public strictDepBuilds?: boolean;

  /**
   * Behavior of `pnpm run` / `pnpm exec` when the lockfile drifts from
   * `node_modules`. Use `"install"` (or `false`) for non-TTY callers.
   *
   * @see https://pnpm.io/settings#verifydepsbeforerun
   */
  public verifyDepsBeforeRun?: "install" | "warn" | "error" | "prompt" | false;

  /**
   * Catalog enforcement mode. Introduced in pnpm 10.12.1.
   *
   * @see https://pnpm.io/settings#catalogmode
   */
  public catalogMode?: "manual" | "prefer" | "strict";

  /**
   * Whether unused catalog entries are pruned on install.
   * Introduced in pnpm 10.15.
   *
   * @see https://pnpm.io/settings#cleanupunusedcatalogs
   */
  public cleanupUnusedCatalogs?: boolean;

  /**
   * Whether `minimumReleaseAge` resolution fails when no version satisfies
   * the age constraint. Introduced in pnpm 11.0.
   *
   * @default false
   *
   * @see https://pnpm.io/settings#minimumreleaseagestrict
   */
  public minimumReleaseAgeStrict?: boolean;

  /**
   * Whether `minimumReleaseAge` skips the age check for registries lacking
   * timestamp metadata. Introduced in pnpm 11.0.
   *
   * @see https://pnpm.io/settings#minimumreleaseageignoremissingtime
   */
  public minimumReleaseAgeIgnoreMissingTime?: boolean;

  /**
   * Values for every curated setting the consumer supplied, keyed by setting
   * name. A setting left undefined is absent from this record and therefore
   * omitted from the generated YAML, so consumers who configure nothing get
   * byte-identical output.
   */
  private readonly curatedSettings: { [settingName: string]: unknown };

  /**
   * Verbatim passthrough settings supplied via `additionalSettings`.
   */
  private readonly additionalSettings: { [settingName: string]: unknown };

  constructor(project: Project, options: PnpmWorkspaceOptions = {}) {
    super(project);

    /***************************************************************************
     *
     * CLEAR package,json
     *
     * It appears that if ANY pnpm settings exist in the package.json file, all
     * of the setting in the pnpm-workspace.yaml file are ignored. so we need to
     * clear out anything that was placed into the package.json file.
     *
     **************************************************************************/

    project.tryFindObjectFile("package.json")?.addDeletionOverride("pnpm");

    /***************************************************************************
     *
     * Setup pnpm-workspace.yaml file.
     *
     * Now that the package.json file is cleared of any pnpm settings, we can
     * safely write settings to the pnpm-workspace.yaml file.
     *
     **************************************************************************/

    /**
     * Set filename to default if not provided.
     */
    this.fileName = options.fileName ?? "pnpm-workspace.yaml";

    /**
     * Set minimum release age; default ONE_WEEK when not provided.
     */
    this.minimumReleaseAge =
      options.minimumReleaseAge ?? MINIMUM_RELEASE_AGE.ONE_WEEK;

    /**
     * Set minimum release age exclude to empty array if not provided
     */
    this.minimumReleaseAgeExclude = options.minimumReleaseAgeExclude
      ? ["@codedrifters/*", ...options.minimumReleaseAgeExclude]
      : ["@codedrifters/*"];

    /**
     * Set only built dependencies to empty array if not provided
     */
    this.onlyBuiltDependencies = options.onlyBuiltDependencies
      ? options.onlyBuiltDependencies
      : [];

    /**
     * Set ignored built dependencies to empty array if not provided
     */
    this.ignoredBuiltDependencies = options.ignoredBuiltDependencies
      ? options.ignoredBuiltDependencies
      : [];

    /**
     * Set allowBuilds map; default empty object when not provided.
     */
    this.allowBuilds = options.allowBuilds ?? {};

    /**
     * Store additional subproject paths if provided
     */
    this.subprojects = options.subprojects ?? [];

    /**
     * Store catalog if provided
     */
    this.defaultCatalog = options.defaultCatalog;

    /**
     * Store named catalogs if provided
     */
    this.namedCatalogs = options.namedCatalogs;

    /**
     * Store pnpm 11 headless / non-TTY options. Each is left undefined when
     * the consumer doesn't supply it so the generated YAML omits the key
     * and pnpm's built-in default applies.
     */
    this.confirmModulesPurge = options.confirmModulesPurge;
    this.strictDepBuilds = options.strictDepBuilds;
    this.verifyDepsBeforeRun = options.verifyDepsBeforeRun;

    /**
     * Store catalog tuning options. Same omit-when-undefined contract.
     */
    this.catalogMode = options.catalogMode;
    this.cleanupUnusedCatalogs = options.cleanupUnusedCatalogs;

    /**
     * Store minimumReleaseAge tuning options. The omit-when-undefined contract
     * applies per-option here, not to the block as a whole:
     * `minimumReleaseAgeStrict` defaults to `false` and therefore always emits
     * into the generated YAML, while `minimumReleaseAgeIgnoreMissingTime` is
     * omitted when undefined so pnpm's built-in default applies.
     */
    this.minimumReleaseAgeStrict = options.minimumReleaseAgeStrict ?? false;
    this.minimumReleaseAgeIgnoreMissingTime =
      options.minimumReleaseAgeIgnoreMissingTime;

    /**
     * Collect the curated settings the consumer actually supplied. Undefined
     * values are never recorded, which is what keeps the omit-when-undefined
     * contract (and therefore snapshot stability) intact.
     *
     * The four bespoke settings are collected here too so `setting()` can
     * read them back uniformly; their *emission* is handled separately
     * because each one composes with or supersedes another key.
     */
    this.curatedSettings = {};
    for (const key of [
      ...CURATED_SETTING_KEYS,
      "neverBuiltDependencies",
      "catalogPrune",
      "update",
      "updateConfig",
    ] as ReadonlyArray<keyof PnpmWorkspaceOptions>) {
      const value = options[key];
      if (value !== undefined) {
        this.curatedSettings[key] = value;
      }
    }

    /**
     * Validate the passthrough tier. A key that configulator already emits
     * would silently shadow (or be shadowed by) the typed field, so reject
     * it up front and name the option to use instead.
     */
    this.additionalSettings = { ...options.additionalSettings };
    for (const settingName of Object.keys(this.additionalSettings)) {
      const typedOption = RESERVED_YAML_KEYS[settingName];
      if (typedOption !== undefined) {
        throw new Error(
          `additionalSettings["${settingName}"] collides with a key that ${this.constructor.name} already emits. ` +
            `Use the \`${typedOption}\` option instead of passing "${settingName}" through additionalSettings.`,
        );
      }
    }

    /**
     * In case that this file is in a package, don't package it.
     */
    project.addPackageIgnore(this.fileName);

    /***************************************************************************
     *
     * RECONCILE with projen's native pnpm-workspace generation.
     *
     * projen 0.101+ unconditionally registers its own `PnpmWorkspaceYaml`
     * component (writing `pnpm-workspace.yaml`) for every pnpm project. This
     * richer PnpmWorkspace owns the file instead, so remove projen's native
     * component first to avoid projen's "there is already a file" synth error.
     * `tryRemoveFile` is a no-op on older projen that never registered it.
     *
     **************************************************************************/

    project.tryRemoveFile(this.fileName);

    /**
     * Write pnpm workspace file
     */
    new YamlFile(this.project, this.fileName, {
      obj: () => {
        const pnpmConfig: any = {};
        const packages = new Array<string>();

        /**
         * Add projen subprojects to the packages array.
         */
        for (const subproject of project.subprojects) {
          // grab the relative out directory
          packages.push(relative(this.project.outdir, subproject.outdir));
        }

        /**
         * Add additional subproject paths provided via options.
         * Use a Set to deduplicate paths to avoid including the same
         * subproject multiple times.
         */
        const packageSet = new Set(packages);
        for (const subprojectPath of this.subprojects) {
          packageSet.add(subprojectPath);
        }

        /**
         * Convert back to array if we added any additional paths
         */
        if (this.subprojects.length > 0) {
          packages.length = 0;
          packages.push(...packageSet);
        }

        /**
         * Set minimum release age.
         */
        pnpmConfig.minimumReleaseAge = this.minimumReleaseAge;

        /**
         * Set minimum release age exclude if any are provided.
         */
        if (this.minimumReleaseAgeExclude.length > 0) {
          pnpmConfig.minimumReleaseAgeExclude = this.minimumReleaseAgeExclude;
        }

        /**
         * Build the merged allowBuilds map. Deprecated and adjacent
         * build-blocking fields translate silently, in precedence order:
         *   - onlyBuiltDependencies entries become allowBuilds[pkg] = true
         *   - ignoredBuiltDependencies entries become allowBuilds[pkg] = false
         *   - neverBuiltDependencies entries become allowBuilds[pkg] = false
         * Explicit `allowBuilds` entries always win on key collisions.
         *
         * Folding `neverBuiltDependencies` into the same map keeps the
         * derived legacy allow/deny arrays consistent with it — otherwise a
         * package listed there could still surface in the emitted
         * `onlyBuiltDependencies` allow-list and contradict itself.
         */
        const neverBuiltDependencies = (this.curatedSettings
          .neverBuiltDependencies ?? []) as Array<string>;

        const mergedAllowBuilds: { [packageName: string]: boolean } = {};
        for (const pkg of this.onlyBuiltDependencies) {
          mergedAllowBuilds[pkg] = true;
        }
        for (const pkg of this.ignoredBuiltDependencies) {
          mergedAllowBuilds[pkg] = false;
        }
        for (const pkg of neverBuiltDependencies) {
          mergedAllowBuilds[pkg] = false;
        }
        for (const [pkg, allowed] of Object.entries(this.allowBuilds)) {
          mergedAllowBuilds[pkg] = allowed;
        }

        /**
         * Emit hybrid YAML for pnpm 10/11 cross-version compatibility. The
         * legacy `onlyBuiltDependencies` / `ignoredBuiltDependencies` arrays
         * are derived from the final merged map (not echoed from input)
         * so an explicit `allowBuilds: { foo: true }` still produces the
         * legacy keys, and explicit-allowBuilds precedence flows through.
         *
         * pnpm 10 silently ignores unknown YAML keys and falls through to
         * its post-Rspack-attack default of "block ALL build scripts" when
         * only `allowBuilds` is present — emitting the legacy arrays keeps
         * pnpm-10 consumers working. pnpm 11 honors `allowBuilds` directly.
         *
         * Both legacy arrays are sorted alphabetically for deterministic
         * snapshot output.
         */
        const allowList: Array<string> = [];
        const denyList: Array<string> = [];
        for (const [pkg, allowed] of Object.entries(mergedAllowBuilds)) {
          if (allowed) {
            allowList.push(pkg);
          } else {
            denyList.push(pkg);
          }
        }
        allowList.sort();
        denyList.sort();

        if (allowList.length > 0) {
          pnpmConfig.onlyBuiltDependencies = allowList;
        }
        if (denyList.length > 0) {
          pnpmConfig.ignoredBuiltDependencies = denyList;
        }

        /**
         * Emit `neverBuiltDependencies` verbatim alongside the derived keys.
         * It is a pnpm setting in its own right, so the array is preserved
         * as supplied even though its entries also appear as `false` in the
         * merged map above.
         */
        if (neverBuiltDependencies.length > 0) {
          pnpmConfig.neverBuiltDependencies = neverBuiltDependencies;
        }

        if (Object.keys(mergedAllowBuilds).length > 0) {
          pnpmConfig.allowBuilds = mergedAllowBuilds;
        }

        /**
         * Set default catalog if provided.
         */
        if (
          this.defaultCatalog &&
          Object.keys(this.defaultCatalog).length > 0
        ) {
          pnpmConfig.catalog = this.defaultCatalog;
        }

        /**
         * Set named catalogs if provided.
         */
        if (this.namedCatalogs && Object.keys(this.namedCatalogs).length > 0) {
          pnpmConfig.catalogs = this.namedCatalogs;
        }

        /**
         * Emit each pnpm 11 tuning key only when the consumer supplied a
         * value. Omitting the key preserves pnpm's built-in default for
         * consumers that don't opt in, and keeps the omitted-default
         * snapshot stable.
         */
        if (this.confirmModulesPurge !== undefined) {
          pnpmConfig.confirmModulesPurge = this.confirmModulesPurge;
        }
        if (this.strictDepBuilds !== undefined) {
          pnpmConfig.strictDepBuilds = this.strictDepBuilds;
        }
        if (this.verifyDepsBeforeRun !== undefined) {
          pnpmConfig.verifyDepsBeforeRun = this.verifyDepsBeforeRun;
        }
        if (this.catalogMode !== undefined) {
          pnpmConfig.catalogMode = this.catalogMode;
        }
        /**
         * `catalogPrune` is pnpm's current spelling for `cleanupUnusedCatalogs`
         * and wins whenever both are supplied, mirroring pnpm's own
         * `catalogPrune ??= cleanupUnusedCatalogs` resolution. Only the
         * winning key is emitted: emitting both would be redundant, and
         * emitting the deprecated spelling when the modern one was requested
         * would misrepresent the consumer's intent. When only the deprecated
         * option is supplied it is emitted unchanged, so consumers on older
         * pnpm majors keep working.
         */
        const catalogPrune = this.curatedSettings.catalogPrune as
          boolean | undefined;
        if (catalogPrune !== undefined) {
          pnpmConfig.catalogPrune = catalogPrune;
        } else if (this.cleanupUnusedCatalogs !== undefined) {
          pnpmConfig.cleanupUnusedCatalogs = this.cleanupUnusedCatalogs;
        }
        if (this.minimumReleaseAgeStrict !== undefined) {
          pnpmConfig.minimumReleaseAgeStrict = this.minimumReleaseAgeStrict;
        }
        if (this.minimumReleaseAgeIgnoreMissingTime !== undefined) {
          pnpmConfig.minimumReleaseAgeIgnoreMissingTime =
            this.minimumReleaseAgeIgnoreMissingTime;
        }

        /**
         * Emit every curated setting the consumer supplied, in the fixed
         * order declared by CURATED_SETTING_KEYS. Settings the consumer left
         * alone were never recorded, so they stay out of the YAML entirely
         * and pnpm's own default applies.
         */
        for (const settingName of CURATED_SETTING_KEYS) {
          const value = this.curatedSettings[settingName];
          if (value !== undefined) {
            pnpmConfig[settingName] = value;
          }
        }

        /**
         * `update` supersedes the deprecated `updateConfig`. Unlike the
         * catalog pair, pnpm actively warns when it sees both keys ("the
         * deprecated updateConfig setting is ignored in favor of update"),
         * so emitting both would print a warning on every install. Emit only
         * the winner.
         */
        const update = this.curatedSettings.update;
        const updateConfig = this.curatedSettings.updateConfig;
        if (update !== undefined) {
          pnpmConfig.update = update;
        } else if (updateConfig !== undefined) {
          pnpmConfig.updateConfig = updateConfig;
        }

        /**
         * Passthrough tier, emitted last. Keys were validated against
         * RESERVED_YAML_KEYS at construction, so nothing here can shadow a
         * typed field or a structural key.
         */
        for (const [settingName, value] of Object.entries(
          this.additionalSettings,
        )) {
          pnpmConfig[settingName] = value;
        }

        /**
         * Return the final pnpm config object.
         */
        return {
          ...(packages.length > 0 ? { packages } : {}),
          ...(pnpmConfig ? { ...pnpmConfig } : {}),
        };
      },
    });
  }

  /**
   * Read back a configured pnpm setting by name.
   *
   * Covers both tiers: curated settings added since the original typed
   * surface, and verbatim `additionalSettings` passthrough keys. Returns
   * `undefined` when the setting was not configured, which is the same
   * signal the emitter uses to omit the key from the generated YAML.
   *
   * The fifteen original options (`minimumReleaseAge`, `allowBuilds`,
   * `defaultCatalog`, and friends) remain available as public fields and are
   * not served by this accessor.
   *
   * @param settingName - The pnpm setting name, e.g. `"overrides"`.
   * @returns The configured value, or `undefined` when unset.
   */
  public setting(settingName: string): unknown {
    if (settingName in this.curatedSettings) {
      return this.curatedSettings[settingName];
    }
    return this.additionalSettings[settingName];
  }
}

/**
 * @deprecated Use `MINIMUM_RELEASE_AGE` instead. This alias will be removed in a future major release.
 */
export const MIMIMUM_RELEASE_AGE = MINIMUM_RELEASE_AGE;
